import React, {CSSProperties, useCallback, useEffect, useRef, useState} from 'react';
import isUrl from "is-url";
import {NavigationProps} from "../constants/Props";
import Button from "../components/Button";
import Screen from "../components/Screen";
import Image from "../components/Image";
import AdvanceTextInput from "../components/AdvanceTextInput";
import Box, {Direction} from "../components/Box";
import classNames from "classnames";
import textStyles from '../css/Text.module.css';
import avatarStyles from '../css/Avatar.module.css';
import buttonStyles from '../css/Button.module.css';
import icons from '../css/Icon.module.css';
import iconStyles from '../css/Icon.module.css';
import ButtonBar from "../components/ButtonBar";
import Icon from "../components/Icon";
import {ILandingPageStore} from "../stores/LandingPageStore";
import styles from "../css/App.module.css";
import Text from "../components/Text";
import {observer} from "mobx-react";
import poweredBySelectSVG from '../images/powered-by-select.svg';
import facebookSVG from '../images/facebook.svg';
import selectLogo from '../images/select-logo.png';
import SVG from "react-inlinesvg";
import Modals, {ModalsRefProps} from "../components/Modals";
import {toI18N} from "../utils/I18n";
import {createInvalidImageFunction, createOpenModalFunction, usePrevious} from "../utils/HooksHelper";
// @ts-ignore
import initials from "initials";
import {getRandomAvatarBackgroundColor} from "../utils/CSSUtils";
import Alert, {AlertRefProps} from "../components/Alert";
import {isMobile} from "react-device-detect";
import * as Analytics from "../utils/Analytics";

interface AvatarWithTextProps {
    title: string,
    color?: string,
    subtitle?: string,
    avatarClassName: string,
    src?: string,
    topMargin?: number,
    bottomMargin?: number,
}

export function AvatarWithText(props: AvatarWithTextProps) {

    const {title, color, src, subtitle, avatarClassName, bottomMargin, topMargin} = props;
    const textStyle: CSSProperties = color ? {color} : {};

    let avatar;
    if (src) {
        avatar = <img src={src} alt={title}/>;
    } else {
        const abbr = initials(title);
        const backgroundColor = getRandomAvatarBackgroundColor(abbr);
        avatar = (
            <div className={avatarStyles.userTextAvatar} style={{backgroundColor: backgroundColor}}>
                <span>{abbr}</span>
            </div>
        );
    }

    return (
        <Box center shrink direction={Direction.ROW} topMargin={topMargin} bottomMargin={bottomMargin}>
            <Box shrink center>
                <div className={classNames(avatarStyles.avatar, avatarClassName)}>
                    {avatar}
                </div>
            </Box>
            <Box center flex direction={Direction.Column} leftMargin={12}>
                {
                    title &&
                    <h3 style={textStyle}
                        className={classNames(textStyles.h3Xs, textStyles.avatarWithTextTitle)}>{title}</h3>
                }
                {
                    subtitle &&
                    <h3 style={textStyle} className={classNames(textStyles.h3Xs, textStyles.avatarWithTextSubtitle)}>{subtitle}</h3>
                }
            </Box>
        </Box>
    );
}

export interface ItemWithAvatar {
    name: string,
    avatar: string
}

interface Props extends NavigationProps {
    store: ILandingPageStore;
    isSharedPreview?: boolean,
    user?: ItemWithAvatar,
    group: ItemWithAvatar
    title?: string,
    subtitle?: string,
    isPreviewSlider?: boolean,
    setIsEditing?: (isEditing: (value: boolean) => boolean) => void,
}

function LandingPageScreen(props: Props) {

    // Props

    let {
        onBack,
        setIsEditing,
        isSharedPreview,
        user,
        title,
        subtitle,
        group,
        onNext,
        isPreviewSlider,
        store: {
            backgroundImage,
            setLocalBackgroundImage,
            setLocalLogo,
            backgroundColor,
            textColor,
            setTextColor,
            setTagline,
            tagline,
            logo,
            removeBackgroundImage,
            setURLBackgroundImage,
            removeLogo,
            setBackgroundColor,
            setURLLogo,
            canProceed
        },
        isActive,
    } = props;

    const prevIsActive = usePrevious(isActive);
    useEffect(() => {
        if (!prevIsActive && isActive) {
            Analytics.logEvent(Analytics.events.land_on_landing_screen_page);
        }
    });


    // State

    const [isPreview, setIsPreview] = useState(isPreviewSlider || false);

    // Ref

    const modalsRef = useRef<ModalsRefProps>(null);
    const alertRef = useRef<AlertRefProps>(null);

    // Callbacks

    const toggleIsPreview = useCallback(() => {
        setIsEditing && setIsEditing(v => !v);
        !isSharedPreview && setIsPreview(v => !v);
    }, [isSharedPreview, setIsEditing]);

    const onOpenImageModal = useCallback(
        createOpenModalFunction(modalsRef, setLocalLogo, setURLLogo, removeLogo, logo),
        [setLocalLogo, setURLLogo, logo, removeLogo],
    );

    const onOpenBackgroundImageModal = useCallback(
        createOpenModalFunction(modalsRef, setLocalBackgroundImage, setURLBackgroundImage, removeBackgroundImage, backgroundImage),
        [setLocalBackgroundImage, setURLBackgroundImage, removeBackgroundImage, backgroundImage],
    );

    const onClickTextColorPicker = useCallback(
        () => {
            modalsRef && modalsRef.current && modalsRef.current.showColorPickerModal(
                toI18N('color_picker_text_color_title'), setTextColor, textColor
            );
        },
        [setTextColor, textColor],
    );

    const handleOnError = useCallback(
        createInvalidImageFunction(alertRef, removeLogo),
        [removeLogo],
    );

    const handleOnBackgroundError = useCallback(
        createInvalidImageFunction(alertRef, removeBackgroundImage),
        [removeBackgroundImage],
    );

    const onClickBackgroundColorPicker = useCallback(
        () => {
            modalsRef && modalsRef.current && modalsRef.current.showColorPickerModal(
                toI18N('landing_page_color_picker_background_color_title'), setBackgroundColor, backgroundColor
            );
        },
        [setBackgroundColor, backgroundColor],
    );

    const renderButtonBar = () => {
        if (isPreviewSlider) {
            return (
                <ButtonBar right>

                    <Button primary onClick={onOpenBackgroundImageModal}>
                        <Icon white name={icons.iconPhoto}/>
                    </Button>
                    <Button primary onClick={onClickTextColorPicker}>
                        <Icon white name={icons.iconText}/>
                    </Button>
                    <Button primary showBadge={!backgroundColor} onClick={onClickBackgroundColorPicker}>
                        <Icon white name={icons.iconBrush}/>
                    </Button>
                    <Button disabled={!canProceed} primary onClick={toggleIsPreview}>
                        <Icon white name={icons.iconTick}/>
                    </Button>
                </ButtonBar>
            )
        }
        return (
            <ButtonBar right>
                <Button primary onClick={toggleIsPreview}>
                    <Icon white name={icons.iconEye}/>
                </Button>
                <Button primary onClick={onOpenBackgroundImageModal}>
                    <Icon white name={icons.iconPhoto}/>
                </Button>
                <Button primary onClick={onClickTextColorPicker}>
                    <Icon white name={icons.iconText}/>
                </Button>
                <Button primary showBadge={!backgroundColor} onClick={onClickBackgroundColorPicker}>
                    <Icon white name={icons.iconBrush}/>
                </Button>
                <Button primary disabled={!canProceed} onClick={onNext}>
                    <Icon white name={icons.iconArrowRight}/>
                </Button>
            </ButtonBar>
        )
    };

    return (
        <Screen
            onBackgroundError={handleOnBackgroundError}
            title={title || "landing_screen_header_title"}
            subtitle={subtitle || "landing_screen_header_subtitle"}
            backgroundImage={backgroundImage ? backgroundImage.path : undefined}
            onBackgroundIsLoad={backgroundImage ? backgroundImage.setIsLoaded : undefined}
            backgroundColor={backgroundColor}
            hideLogo={isPreview}
            hideBack={isPreviewSlider || isPreview}
            onBack={onBack}
            hideNext>

            <Alert ref={alertRef}/>

            <Modals
                id={'landing-page-screen'}
                showCurtain={isPreview}
                setIsPreview={setIsPreview}
                onCurtainClick={toggleIsPreview}
                modalTextInputValidation={isUrl}
                ref={modalsRef}/>

            <Box center direction={Direction.Column} shrink>

                <Box center style={{marginTop: isMobile ? 65 : 75}}>
                    <Image
                        isPreview={isPreview}
                        onClick={onOpenImageModal}
                        onLoad={logo ? logo.setIsLoaded : undefined}
                        onError={handleOnError}
                        src={logo ? logo.path : isPreview ? selectLogo : undefined}
                        height={100}
                        minWidth={155}/>
                </Box>

                <Box center topMargin={10}>
                    <AdvanceTextInput
                        onChange={setTagline}
                        allowChangingColor={false}
                        shrink
                        value={tagline}
                        maxLength={40}
                        xs
                        id={"tagline-input-landing-page-screen"}
                        placeholder={toI18N('landing_tagline_placeholder')}
                        color={textColor}
                        isPreview={isPreview}/>
                </Box>

                <Box center topMargin={10}>
                    <Box direction={Direction.Clear} rightMargin={4}>
                        <SVG
                            style={{
                                fill: textColor
                            }}
                            className={classNames(iconStyles.icon, iconStyles.svgFill)}
                            src={poweredBySelectSVG}/>
                    </Box>
                    <Text size={12} color={textColor}>{toI18N('powered_by_select')}</Text>
                </Box>

            </Box>

            <Box center grow>
                <Box center sideMargin>
                    {
                        (() => {
                            const bottomMargin = 10;
                            return user ?
                                <AvatarWithText
                                    color={textColor}
                                    avatarClassName={avatarStyles.inviterAvatar}
                                    bottomMargin={bottomMargin}
                                    title={user.name}
                                    src={user.avatar}
                                    subtitle={toI18N('landing_user_invited_you')}/>
                                :
                                <Box bottomMargin={bottomMargin} center>
                                    <Text size={16} fontWeight={600}
                                          color={textColor}>{toI18N('landing_you_were_invited')}</Text>
                                </Box>
                        })()
                    }
                    <div className={styles.separator}/>
                    <AvatarWithText
                        topMargin={10}
                        color={textColor}
                        avatarClassName={avatarStyles.groupAvatar}
                        title={group.name}
                        src={group.avatar}
                    />
                </Box>
            </Box>

            <Box center shrink direction={Direction.Column} sideMargin bottomMargin={30}>
                <Box center bottomMargin={10}>
                    <Box center
                         fullWidth
                         className={buttonStyles.facebookButton}
                         style={
                             textColor ?
                                 {
                                     border: `1px ${textColor} solid`,
                                     color: textColor
                                 } :
                                 undefined
                         }>
                        <Box shrink>
                            <SVG
                                style={{
                                    fill: textColor
                                }}
                                className={classNames(iconStyles.icon)}
                                src={facebookSVG}/>
                        </Box>
                        <Box grow center>
                            {toI18N('landing_connect_with_facebook')}
                        </Box>
                    </Box>
                </Box>
                <Box fullWidth center bottomMargin={10}>
                    <Text color={textColor} size={12}>
                        {toI18N('landing_permissions')}
                    </Text>
                </Box>
                <Box center bottomMargin={20}>
                    <Box fullWidth center className={buttonStyles.signUpInButton}>
                        {toI18N('landing_signup_login')}
                    </Box>
                </Box>
                <Box fullWidth center>
                    <Text color={textColor} size={14}>
                        {toI18N('landing_cant_receive_sms')}
                        <span className={textStyles.colorBold}>{toI18N('landing_login_with_password')}</span>
                    </Text>
                </Box>
            </Box>
            {!isPreview && renderButtonBar()}
        </Screen>
    );
}

export default observer(LandingPageScreen);
